import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ObjectSchema } from 'yup';
import { useAtomValue } from 'jotai';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldGroup } from '../../../../../components/FieldGroup/FieldGroup';
import { FormField } from '../../../../../components/FormField/FormField';
import { NumericInput } from '../../../../../components/Inputs/NumericInput';
import { LabelInput } from '../../../../../components/LabelInput/LabelInput';
import { Loader } from '../../../../../components/Loader/Loader';
import { MuiMultiselect } from '../../../../../components/MuiMultiselect/MuiMultiselect';
import { MuiSingleSelect, Option } from '../../../../../components/MuiSingleSelect/MuiSingleSelect';
import { IDealDataModel } from '../../../../../data-models/deal.data-model';
import { useDealBoardConfig } from '../../../../../services/queries-hooks/MaggieDealQueriesHooks';
import { activeUsersAtom, selectableFundsAtom } from '../../../../../services/state/AppConfigStateJ';
import { DealTypeSelect } from '../../../../Finance/form-components/DealTypeSelect';
import { useRoundSelector } from '../../../../Finance/hooks/useRoundSelector';
import { useLabelActions } from '../../../hooks/useLabelActions';
import { useDeal } from '../../../providers/DealProvider';
import { dealLabelsState } from '../../../state/DealboardDataState';
import { dealInitialValues as initialValues } from './dealInitialValues';
import { DealSchema } from './DealSchema';

const Container = styled('div')``;

const FieldWrapper = styled('div')`
  margin-bottom: 15px;
`;

const FormWrapper = styled('form')`
  display: flex;
  flex-direction: column;
  padding: 0 24px;
  background-color: ${({ theme }) => theme.colors.primary[0]};
  border: 1px solid ${({ theme }) => theme.colors.primary[20]};
`;

const DoubleFieldWrapper = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const GcMinInputWrapper = styled('div')`
  width: 56%;
`;

const GcMaxInputWrapper = styled('div')`
  width: 41%;
`;

// Form-local value types. The deal data-model types several of these as
// non-null numbers, but the form holds nulls for unset fields (matching the
// yup schema's .nullable()); values are cast back to the data-model shape at
// the setDealInfo boundary.
interface DealFormValues {
  dealTypeId?: number | null;
  roundId?: number | null;
  fundIds?: number[];
  dealLeadId?: number | null;
  dealTeamIds?: number[];
  labels?: string[];
  raiseAmount?: number | null;
  gcAmountMin?: number | null;
  gcAmountMax?: number | null;
  totalPic?: number | null;
}

export const DealForm = () => {
  const { setDealInfo, setIsDealFormValid, dealInfo } = useDeal();
  const { data: dealBoardConfigData, isLoading: isLoadingDealBoardConfig } = useDealBoardConfig();
  const users = useAtomValue(activeUsersAtom).map((u) => ({ id: u.id, value: u.name }));
  const { roundsOptions } = useRoundSelector(true);
  const funds = useAtomValue(selectableFundsAtom);
  const [selectedFunds, setSelectedFunds] = useState<Option[]>([]);

  const labelsLoadable = useAtomValue(dealLabelsState);
  const allLabels = useMemo(() => {
    return labelsLoadable.state === 'hasData' ? labelsLoadable.data : [];
  }, [labelsLoadable]);

  const labelOptions = useMemo(() => {
    if (!allLabels || !allLabels.length) {
      return [];
    }
    return allLabels.map((l) => ({ id: l.name, value: l.name }));
  }, [allLabels]);
  const { handleCreateLabel: createAndUpdateLabels } = useLabelActions();

  const sortedFundOptions: { id: number; value: string }[] = useMemo(() => {
    if (!funds) {
      return [];
    }
    return [...funds]
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      .map(({ id, name }) => ({ id, value: name }));
  }, [funds]);

  const [touched, setTouched] = useState<Partial<Record<keyof typeof initialValues, boolean>>>({});

  const {
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors, isValid },
  } = useForm<DealFormValues>({
    defaultValues: (dealInfo ?? initialValues) as DealFormValues,
    mode: 'all',
    resolver: yupResolver(DealSchema as ObjectSchema<DealFormValues>),
  });

  const values = watch();

  const onChangeDealLead = useCallback(
    (e: SyntheticEvent, newVal: Option | null) => {
      const user = users?.find((u) => u.id === newVal?.id);
      setValue('dealLeadId', user?.id, { shouldValidate: true });
    },
    [setValue, users]
  );

  const onChangeDealTeam = useCallback(
    (e: SyntheticEvent, vals: Option[] | undefined) => {
      if (!vals) {
        setValue('dealTeamIds', [], { shouldValidate: true });
        return;
      }
      setValue(
        'dealTeamIds',
        vals.map((v) => Number(v.id)),
        { shouldValidate: true }
      );
    },
    [setValue]
  );
  const onChangeLabels = useCallback(
    (vals: string[]) => {
      setValue('labels', vals, { shouldValidate: true });
    },
    [setValue]
  );

  const onCreateLabel = useCallback(
    async (val: string) => {
      const currentLabels = values?.labels;
      onChangeLabels([...(currentLabels ?? []), val]);
      const newLabel = await createAndUpdateLabels(val);
      if (!newLabel) {
        onChangeLabels(currentLabels ?? []);
      }
    },
    [createAndUpdateLabels, onChangeLabels, values?.labels]
  );

  const handleDisplayError = (key: keyof typeof initialValues) => {
    return touched[key] ? (errors[key as keyof typeof errors]?.message ?? '') : '';
  };

  useEffect(() => {
    setValue(
      'fundIds',
      selectedFunds.map(({ id }) => Number(id)),
      { shouldValidate: true }
    );
  }, [selectedFunds, setValue]);

  // Keep the deal atom in sync with the form. Use a watch subscription (not the
  // rendered `values`) so this fires only on actual field changes, avoiding a
  // setState-on-every-render loop.
  useEffect(() => {
    setDealInfo(getValues() as Partial<IDealDataModel>);
    const subscription = watch((value) => setDealInfo(value as Partial<IDealDataModel>));
    return () => subscription.unsubscribe();
  }, [watch, getValues, setDealInfo]);

  useEffect(() => {
    setIsDealFormValid(isValid);
  }, [isValid, setIsDealFormValid]);

  if (!users || !dealBoardConfigData || isLoadingDealBoardConfig) {
    return <Loader height={'30rem'} />;
  }

  return (
    <Container>
      <FormWrapper onSubmit={handleSubmit((val) => setDealInfo(val as Partial<IDealDataModel>))}>
        <FieldGroup hasBorder={false} label='General Information'>
          <FieldWrapper>
            <FormField
              required
              label={'Deal Type'}
              error={handleDisplayError('dealTypeId')}
              labelWidth='12rem'
            >
              <DealTypeSelect
                value={values.dealTypeId ?? null}
                onChange={(val) => setValue('dealTypeId', val, { shouldValidate: true })}
                onFocus={() => setTouched((prev) => ({ ...prev, dealTypeId: true }))}
              />
            </FormField>
          </FieldWrapper>
          <FormField required label={'Stage'} error={handleDisplayError('roundId')} labelWidth='12rem'>
            <MuiSingleSelect
              disablePortal
              options={roundsOptions}
              value={roundsOptions?.find((sround) => sround.id === values.roundId) ?? null}
              onChange={(_, val) =>
                setValue('roundId', val ? Number(val.id) : null, { shouldValidate: true })
              }
              fieldName='roundId'
              onFocus={() => setTouched((prev) => ({ ...prev, roundId: true }))}
              fieldPlaceholder='Select Stage'
              disableClearable
            />
          </FormField>
          <FormField label={'Funds'} error={handleDisplayError('fundIds')} labelWidth='12rem'>
            <MuiMultiselect
              options={sortedFundOptions}
              value={selectedFunds}
              onChange={(_, val) => {
                setSelectedFunds(val as Option[]);
              }}
              disablePortal
              onFocus={() => setTouched((prev) => ({ ...prev, fundIds: true }))}
              fieldName={'fundIds'}
            />
          </FormField>
          <FormField required label={'Deal Lead'} error={handleDisplayError('dealLeadId')} labelWidth='12rem'>
            <MuiSingleSelect
              options={users}
              value={users.find((u) => u.id === values.dealLeadId) ?? null}
              onChange={onChangeDealLead}
              disablePortal
              onFocus={() => setTouched((prev) => ({ ...prev, dealLeadId: true }))}
              fieldPlaceholder='Select Deal Lead'
              fieldName='dealLeadId'
            />
          </FormField>
          <FormField label={'Deal Team'} error={handleDisplayError('dealTeamIds')} labelWidth='12rem'>
            <MuiMultiselect
              options={users}
              fieldName={'dealTeamIds'}
              value={users.filter((u) => values?.dealTeamIds?.includes(u.id)) ?? []}
              onChange={onChangeDealTeam}
              onFocus={() => setTouched((prev) => ({ ...prev, dealTeamIds: true }))}
              fieldPlaceholder='Select Deal Team'
              fullWidth
              disablePortal
            />
          </FormField>
        </FieldGroup>
        <FieldGroup label='Financial Information'>
          <FormField label={`Raise Amount`} error={handleDisplayError('raiseAmount')} labelWidth='12rem'>
            <NumericInput
              name={'raiseAmount'}
              value={values.raiseAmount}
              onChange={(e) => {
                setValue('raiseAmount', e.target.value ? parseFloat(e.target.value) : null, {
                  shouldValidate: true,
                });
              }}
              prefix='$'
              onBlur={() => setTouched((prev) => ({ ...prev, raiseAmount: true }))}
            />
          </FormField>
          <DoubleFieldWrapper>
            <GcMinInputWrapper>
              <FormField
                label={`Investment Amount`}
                error={handleDisplayError('gcAmountMin')}
                labelWidth='248px'
              >
                <NumericInput
                  name={'gcAmountMin'}
                  value={values.gcAmountMin}
                  onChange={(e) => {
                    setValue('gcAmountMin', e.target.value ? parseFloat(e.target.value) : null, {
                      shouldValidate: true,
                    });
                  }}
                  prefix='$'
                  onBlur={() => setTouched((prev) => ({ ...prev, gcAmountMin: true }))}
                />
              </FormField>
            </GcMinInputWrapper>
            <GcMaxInputWrapper>
              <FormField label={`to`} error={handleDisplayError('gcAmountMax')} labelWidth='2.3rem'>
                <NumericInput
                  name={'gcAmountMax'}
                  value={values.gcAmountMax}
                  onChange={(e) => {
                    setValue('gcAmountMax', e.target.value ? parseFloat(e.target.value) : null, {
                      shouldValidate: true,
                    });
                  }}
                  prefix='$'
                  onBlur={() => setTouched((prev) => ({ ...prev, gcAmountMax: true }))}
                />
              </FormField>
            </GcMaxInputWrapper>
          </DoubleFieldWrapper>
          <FormField label={`Total PIC`} error={handleDisplayError('totalPic')} labelWidth='12rem'>
            <NumericInput
              name={'totalPic'}
              value={values.totalPic}
              onChange={(e) => {
                setValue('totalPic', e.target.value ? parseFloat(e.target.value) : null, {
                  shouldValidate: true,
                });
              }}
              prefix='$'
              onBlur={() => setTouched((prev) => ({ ...prev, totalPic: true }))}
            />
          </FormField>
        </FieldGroup>
        <FieldGroup labelMargin={'0'}>
          <FormField label={`Labels`} error={handleDisplayError('labels')} labelWidth='12rem'>
            <LabelInput
              options={labelOptions}
              onCreateLabel={onCreateLabel}
              fieldName='labels'
              value={values?.labels ?? []}
              onChange={onChangeLabels}
              onFocus={() => setTouched((prev) => ({ ...prev, labels: true }))}
              fieldPlaceholder='Add Deal Labels'
              disablePortal
              loading={labelsLoadable.state === 'loading'}
            />
          </FormField>
        </FieldGroup>
      </FormWrapper>
    </Container>
  );
};
